<template>
  <form @submit.prevent="submit">
    <template v-if="canChangeDomain()">
      <FormMessage :errors="errors" v-if="errors" />
      <FormInput
        name="domain"
        v-model="fields.domain"
        testId="domain-input"
        placeholder="Domain"
        :label="`${$t('global.domain')} *`"
        :errors="validation.fields.domain.errors"
        :dirty="validation.fields.domain.dirty"
        :disabled="isSubmitting"
        class="pb-4"
      />
      <Alert
        v-if="isDomainAgnostic()"
        :message="$t('global.site-update-domain-le-message')"
        variant="info"
        :dismissible="false"
      />
    </template>
    <template v-else-if="!isDomainAgnostic()">
      <Alert variant="warning" :dismissible="false">
        <p>{{ $t("global.site-update-domain-forbidden-message") }}</p>
        <ul>
          <li>{{ $t("global.site-update-domain-forbidden-reason-1-message") }}</li>
          <li>{{ $t("global.site-update-domain-forbidden-reason-2-message") }}</li>
          <li>{{ $t("global.site-update-domain-forbidden-reason-3-message") }}</li>
          <li>{{ $t("global.site-update-domain-forbidden-reason-4-message") }}</li>
          <li>{{ $t("global.site-update-domain-forbidden-reason-5-message") }}</li>
        </ul>
      </Alert>
    </template>
    <ErrorState v-else />
    <div class="form-buttons">
      <Btn
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-change-site-domain-button"
      >
        {{ getCloseButtonDisplay() }}
      </Btn>
      <Btn
        v-if="canChangeDomain()"
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="change-site-domain-button"
        class="ml-2"
      >
        {{ $t("global.change-domain") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import ErrorState from "@/components/display/ItemErrorState";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { updateSiteDomain as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "SiteUpdateDomainForm",
  props: {
    site: {
      type: Object,
      required: true,
    },
  },
  components: {
    Alert,
    Btn,
    ErrorState,
    FormInput,
    FormMessage,
  },
  setup(props, { emit, root }) {
    const isSubmitting = ref(false);
    const errors = ref(null);
    const fields = reactive({ domain: props.site.domain });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        domain: {
          domain: root.$t("validation.domain-invalid"),
          required: root.$t("validation.domain-required"),
        },
      }
    );

    const cancel = () => emit("cancel");
    const canChangeDomain = () => props.site.canChangeDomain;
    const getCloseButtonDisplay = () => root.$t(`global.${canChangeDomain() ? "cancel" : "close"}`);
    const isDomainAgnostic = () => props.site.isDomainAgnostic;

    const isSubmittable = () =>
      canChangeDomain() &&
      !isSubmitting.value &&
      validation.errors.length === 0 &&
      fields.domain !== props.site.domain;

    const submit = () => {
      errors.value = null;
      validation
        .validate()
        .then(() => {
          const { domain } = fields;
          isSubmitting.value = true;
          store
            .dispatch("sites/updateDomain", {
              id: props.site.id,
              data: { domain },
            })
            .then(() => {
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.site-updating-domain-message", {
                  original: props.site.domain,
                  new: domain,
                }),
              });
              emit("success");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.site-update-domain-error-message", {
                  original: props.site.domain,
                  new: domain,
                }),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };

    return {
      cancel,
      canChangeDomain,
      errors,
      fields,
      getCloseButtonDisplay,
      isDomainAgnostic,
      isSubmittable,
      isSubmitting,
      submit,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
